import { StatusMessage } from "@/components/auth/AuthFlowComponents"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { Colors } from "@/constants/Colors"
import { completeSupabaseOAuthCallback } from "@/services/oauthServerClient"
import { getSupabaseClient } from "@/services/supabaseClient"
import { router } from "expo-router"
import { useEffect, useState } from "react"
import { Platform } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

type CallbackState = "loading" | "denied" | "error"

export default function SupabaseOAuthCallbackScreen() {
  const [state, setState] = useState<CallbackState>("loading")
  const [message, setMessage] = useState("Finishing sign-in...")

  useEffect(() => {
    let mounted = true

    async function finishSignIn() {
      try {
        if (Platform.OS !== "web" || typeof window === "undefined") {
          router.replace("/auth")
          return
        }

        const client = getSupabaseClient()
        if (!client) {
          throw new Error("Supabase is not configured for this build.")
        }

        const result = await completeSupabaseOAuthCallback(window.location.href)

        if (result.status !== "success") {
          if (mounted) {
            setState(result.status === "denied" ? "denied" : "error")
            setMessage(result.message)
          }
          return
        }

        const { access_token, refresh_token } = result.tokens
        if (!access_token || !refresh_token) {
          throw new Error("OAuth token exchange did not return a usable session.")
        }

        const { data, error } = await client.auth.setSession({
          access_token,
          refresh_token,
        })

        if (error || !data.session) {
          throw error ?? new Error("OAuth token exchange did not create a session.")
        }

        router.replace(result.returnTo as never)
      } catch (error) {
        console.warn("Failed to complete Supabase OAuth callback", error)
        if (mounted) {
          setState("error")
          setMessage(error instanceof Error ? error.message : "Could not finish sign-in.")
        }
      }
    }

    finishSignIn()

    return () => {
      mounted = false
    }
  }, [])

  return (
    <SafeAreaView
      className="flex-1 justify-center px-6"
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <Box
        className="rounded-2xl border p-6"
        style={{
          backgroundColor: Colors.dark.surfaceSecondary,
          borderColor: "rgba(255,255,255,0.08)",
        }}
        testID="auth-callback"
      >
        <VStack className="items-center gap-4">
          {state === "loading" ? (
            <Spinner color={Colors.dark.primary} size="large" testID="auth-callback-spinner" />
          ) : null}
          <Text className="text-center text-xl font-bold text-white">
            {state === "loading" ? "Signing you in" : "Sign-in incomplete"}
          </Text>
          <StatusMessage
            message={message}
            testID="auth-callback-message"
            tone={state === "loading" ? "info" : "warning"}
          />
          {state !== "loading" ? (
            <Button testID="auth-callback-back" onPress={() => router.replace("/auth")}>
              <ButtonText>Back to sign in</ButtonText>
            </Button>
          ) : null}
        </VStack>
      </Box>
    </SafeAreaView>
  )
}
